import { AppBundle } from './app-bundles';
import { sanitizeAppLoadDiagnosticPath, sentryCaptureAppLoadDiagnostic } from './sentry';

/**
 * Detects runaway reboots of the embedded Journeys (Ember) app.
 *
 * Every Journeys boot constructs a brand-new Ember application — and therefore a
 * brand-new Ember `billing` service — which re-fetches `billing/profile` on
 * startup. If the app boots many times in quick succession, something is
 * remounting it repeatedly (e.g. the non-background legacy/auth routes, or the
 * background context being lost). This mirrors the detection added to the Ember
 * billing service, but lives here because that per-instance detector cannot see
 * across separate Ember app instances.
 *
 * This is diagnostic only: we report to Sentry to confirm the issue, we do not
 * try to prevent the boots.
 */
const JOURNEYS_BOOT_WINDOW_MS = 30_000;
const JOURNEYS_BOOT_THRESHOLD = 5;

/** Which mount path booted the app, so Sentry shows where the reboots come from. */
export type JourneysBootSource = 'background' | 'non-background';

interface JourneysBoot {
  at: number;
  path: string;
}

// Module-level so it survives Ember app reboots and React component remounts —
// the state intentionally lives outside both React and the embedded Ember app.
let boots: JourneysBoot[] = [];

// Single-instance invariant guard (cumulative, not windowed). The contract is exactly
// one boot per page load; a full page reload reloads this module and resets the count.
// So once the legacy second mount is gone, *any* second boot in the same session means
// the invariant broke — e.g. the background-app context provider remounted (root error
// boundary, a stray `key`), or the embedded-app router bridge fell back to full route
// loads. We flag the first such reboot distinctly so it surfaces even though it is far
// below the storm threshold above.
let sessionBootCount = 0;
let firstBootPath: string | null = null;
let reportedUnexpectedReboot = false;

export function recordJourneysBoot(source: JourneysBootSource) {
  const now = Date.now();

  // Record the route the app booted on. Sentry already captures the current URL
  // for the report itself; the value here is the *sequence* of routes across the
  // window. Sanitized (like the diagnostic's own `path`) so query strings and
  // identifier segments (e.g. oauth/reset-password tokens) are not sent.
  const path = sanitizeAppLoadDiagnosticPath(window.location.pathname);

  sessionBootCount += 1;
  firstBootPath ??= path;

  // Flag the first unexpected reboot (the second boot in the session), once. This is a
  // precise guard for the single-instance invariant; the windowed storm detector below
  // still catches runaway loops with their full path sequence.
  if (sessionBootCount >= 2 && !reportedUnexpectedReboot) {
    reportedUnexpectedReboot = true;
    sentryCaptureAppLoadDiagnostic(
      'journeys_unexpected_reboot',
      {
        appBundle: AppBundle.Journeys,
        source,
        sessionBootCount,
        firstBootPath,
        path,
      },
      { level: 'warning' },
    );
  }

  boots.push({ at: now, path });
  // Drop boots that fall outside the detection window.
  boots = boots.filter((boot) => now - boot.at <= JOURNEYS_BOOT_WINDOW_MS);

  if (boots.length < JOURNEYS_BOOT_THRESHOLD) {
    return;
  }

  // sentryCaptureAppLoadDiagnostic rate-limits per kind, so a sustained storm
  // reports at most once every few minutes rather than on every boot.
  sentryCaptureAppLoadDiagnostic(
    'journeys_repeated_boot',
    {
      appBundle: AppBundle.Journeys,
      source,
      bootCount: boots.length,
      windowMs: JOURNEYS_BOOT_WINDOW_MS,
      paths: boots.map((boot) => boot.path),
    },
    { level: 'warning' },
  );
}
